/**
 * The brushed range, as a removable chip in the tab bar (spec §B1). It renders
 * only while a brush is active — the docks used to print "Selection: Whole
 * run" permanently, a line of text that said nothing on every glance it was
 * not wrong (audit C13). Every KPI silently narrows to the brush, so the brush
 * has to be visible and reversible from the dock's own chrome (M4); this is
 * that, at the size of one control.
 *
 * Render it conditionally: `{selActive && <DockSelectionChip … />}`.
 */
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { DOCK_CONTROL, DOCK_TEXT } from './dockStyles';

export interface DockSelectionChipProps {
  /** The formatted range, "12+100 – 12+200". */
  range: string;
  onClear: () => void;
  /** On the chip's range text — `clearance-selection`, `sv-selection`. */
  testId?: string;
  /** On the ✕ — `clearance-clear-selection`, `sv-clear-selection`. */
  clearTestId?: string;
  /** The chip's prefix; defaults to `stationVolumes.panel.selection`. */
  label?: string;
  /** The ✕'s name; defaults to `stationVolumes.panel.clearSelection`. */
  clearLabel?: string;
}

export const DockSelectionChip = ({ range, onClear, testId, clearTestId, label, clearLabel }: DockSelectionChipProps) => {
  const { t } = useTranslation('project');
  const clearName = clearLabel ?? t('stationVolumes.panel.clearSelection');
  return (
    <span className='flex h-6 min-w-0 shrink items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 pr-0.5 pl-2 text-slate-700 dark:border-sky-400/40 dark:bg-sky-400/10 dark:text-stone-200'>
      {/* The prefix goes first when the tab bar is short of room — the range
          IS the selection; the word only names it. */}
      <span className={cn(DOCK_TEXT.metaMuted, 'shrink-0 @max-[1100px]:hidden')}>
        {label ?? t('stationVolumes.panel.selection')}
      </span>
      <span data-testid={testId} className='sv-sta min-w-[9ch] truncate'>
        {range}
      </span>
      <button
        type='button'
        data-testid={clearTestId}
        aria-label={clearName}
        title={clearName}
        onClick={onClear}
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200/70 hover:text-slate-900 dark:text-stone-400 dark:hover:bg-stone-700/70 dark:hover:text-white',
          DOCK_CONTROL.focus,
        )}
      >
        <X className='size-3' aria-hidden />
      </button>
    </span>
  );
};
